/**
 * Launch Calendar — C-mode time-phased execution planning (Doc 102 §2A Future).
 *
 * Answers the portfolio question: "Can we launch what we prioritized?"
 * Timeline of launch windows on shared teams, buildable vs launchable,
 * slip weeks, and counterfactual levers. Reads persisted placements first;
 * re-runs the engine only on demand.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  DollarSign,
  Download,
  Loader2,
  Play,
  RefreshCw,
  Rocket,
  Search,
  Sparkles,
  TrendingUp,
  XCircle,
} from 'lucide-react';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { isPlatformOrSystemAdmin } from '../../types/prioritization/roles';
import { getScenarios } from '../../lib/planningEngineService';
import {
  getLatestPlacements,
  getLatestUtilization,
  getLatestCalendarChangeSet,
  getLatestTimePhasedAssumptions,
  runTimePhasedPlacement,
  runCounterfactual,
  setupTimePhasedPlanning,
  seedKeithSharedTeamCommit,
  seedSamplePortfolio,
  exportPlacementsCsv,
  getLatestFinancialUtilization,
  ensureScenarioFinancialLayer,
  buildTimePhasedInput,
  setAssumptionHumanStatus,
  setPriorityAnchoring,
  shiftPriorityLaunchWeeks,
} from '../../lib/timePhasedPlanningService';
import type { Scenario } from '../../lib/planningEngineModule.types';
import type {
  BucketUtilization,
  CalendarChangeSet,
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
  TimePhasedPlacement,
} from '../../lib/timePhasedPlanning.types';
import type {
  BucketMoneyUtilization,
  FundingAffordability,
} from '../../lib/timePhasedFinancial.types';
import type {
  TimePhasedAssumptionStatus,
  TimePhasedAssumptionsSnapshot,
} from '../../lib/timePhasedAssumptions';
import { getItemCountState } from '../../components/collection/types';
import { TimePhasedModuleGate } from '../../components/planning/timePhased/TimePhasedModuleGate';
import { LaunchTimeline } from '../../components/planning/timePhased/LaunchTimeline';
import { LaunchPlacementInspector } from '../../components/planning/timePhased/LaunchPlacementInspector';
import { CalendarChangeSetPanel } from '../../components/planning/timePhased/CalendarChangeSetPanel';
import { ScenarioAssumptionsPanel } from '../../components/planning/timePhased/ScenarioAssumptionsPanel';
import { ScenarioBindingBadge } from '../../components/planning/ScenarioBindingBadge';
import type { PlacementAnchoring } from '../../lib/placementAnchoring';
import { launchAnswer, rollUpByQuarter } from '../../lib/timePhasedQuarterRollup';
import { DataTable } from '../../components/ds';
import {
  PLACEMENT_STATUS_CLASS,
  PLACEMENT_STATUS_LABELS,
  utilizationHeatClass,
  downloadTextFile,
} from '../../components/planning/timePhased/timePhasedUiShared';

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';

function summarize(placements: TimePhasedPlacement[]) {
  return {
    scheduled: placements.filter((p) => p.placementStatus === 'scheduled').length,
    slipped: placements.filter((p) => p.placementStatus === 'slipped').length,
    blockedOrExhausted: placements.filter(
      (p) => p.placementStatus === 'blocked' || p.placementStatus === 'supply_exhausted',
    ).length,
    buildableNotLaunchable: placements.filter((p) => p.buildable && !p.launchable).length,
  };
}

function FirstRunCard({
  onSetup,
  onDemo,
  onSample,
  canLoadSamples,
  settingUp,
  error,
}: {
  onSetup: () => void;
  onDemo: () => void;
  onSample: () => void;
  /** Sample-data loaders are for platform admins (demos), not for a customer's own workspace. */
  canLoadSamples: boolean;
  settingUp: boolean;
  error: string | null;
}) {
  return (
    <div className={`${CARD} p-8 text-center max-w-xl mx-auto`}>
      <div className="mx-auto h-12 w-12 rounded-full bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center mb-4">
        <Sparkles className="h-6 w-6 text-primary-600 dark:text-primary-400" />
      </div>
      <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
        No launch calendar yet for this scenario
      </h2>
      <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] leading-relaxed">
        One click sets everything up: a planning horizon, built-in workload templates (Standard
        product launch, Partner campaign, Pricing &amp; packaging), a default supply calendar for
        shared teams, and a first placement run. You can refine all of it afterwards.
      </p>
      <div className="mt-5 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          disabled={settingUp}
          onClick={onSetup}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {settingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Set up &amp; run first placement
        </button>
        {canLoadSamples && (
          <>
          <button
            type="button"
            disabled={settingUp}
            onClick={onDemo}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-primary-300 text-primary-700 dark:text-primary-300 dark:border-primary-700 disabled:opacity-50"
          >
            {settingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            Load shared-team sample
          </button>
          <button
            type="button"
            disabled={settingUp}
            onClick={onSample}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-[var(--rule)] text-[var(--text-pri)] dark:text-[var(--text-pri)] dark:border-[var(--rule)] disabled:opacity-50"
          >
            {settingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
            Load sample portfolio
          </button>
          </>
        )}
      </div>
      {canLoadSamples && (
        <p className="mt-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Platform admins only. Shared-team sample: its own scenario and Item Pool of launches with
          tight shared teams. Sample portfolio: a multi-org sample workbook.
        </p>
      )}
      {error && <p className="mt-3 text-xs text-[var(--coral)]">{error}</p>}
    </div>
  );
}

function LaunchCalendarInner() {
  const projectId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId || null);
  const currentRole = useUserStore((s) => s.currentRole);
  const canLoadSamples = currentRole ? isPlatformOrSystemAdmin(currentRole) : false;

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenarioId, setScenarioId] = useState<string>('');
  const [placements, setPlacements] = useState<TimePhasedPlacement[]>([]);
  const [utilization, setUtilization] = useState<BucketUtilization[]>([]);
  const [moneyUtilization, setMoneyUtilization] = useState<BucketMoneyUtilization[]>([]);
  const [affordability, setAffordability] = useState<FundingAffordability | null>(null);
  const [hasPersisted, setHasPersisted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [changeSet, setChangeSet] = useState<CalendarChangeSet | null>(null);
  const [counterError, setCounterError] = useState<string | null>(null);
  const [demoMessage, setDemoMessage] = useState<string | null>(null);
  const [counterLoading, setCounterLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [fundingLoading, setFundingLoading] = useState(false);
  const [assumptions, setAssumptions] = useState<TimePhasedAssumptionsSnapshot | null>(null);
  const [baselineInput, setBaselineInput] = useState<TimePhasedEngineInput | null>(null);
  const [baselineOutput, setBaselineOutput] = useState<TimePhasedEngineOutput | null>(null);
  const inspectorRef = useRef<HTMLDivElement>(null);

  const scenario = scenarios.find((s) => s.id === scenarioId);
  const selectedPlacement = placements.find((p) => p.priorityId === selectedId) ?? null;

  const refreshBaselineForShadow = useCallback(async () => {
    if (!projectId || !scenarioId || placements.length === 0) {
      setBaselineInput(null);
      setBaselineOutput(null);
      return;
    }
    try {
      const input = await buildTimePhasedInput(projectId, scenarioId);
      setBaselineInput(input);
      setBaselineOutput({
        algorithmVersion: 'time_phased_v1',
        placements,
        bucketUtilization: utilization,
        summary: {
          scheduled: placements.filter((p) => p.placementStatus === 'scheduled').length,
          slipped: placements.filter((p) => p.placementStatus === 'slipped').length,
          blocked: placements.filter((p) => p.placementStatus === 'blocked').length,
          unscheduled: placements.filter((p) => p.placementStatus === 'unscheduled').length,
          supplyExhausted: placements.filter((p) => p.placementStatus === 'supply_exhausted').length,
          belowLine: placements.filter((p) => p.placementStatus === 'below_line').length,
          total: placements.length,
        },
      });
    } catch {
      setBaselineInput(null);
      setBaselineOutput(null);
    }
  }, [projectId, scenarioId, placements, utilization]);

  useEffect(() => {
    void refreshBaselineForShadow();
  }, [refreshBaselineForShadow]);

  const handleAssumptionStatus = useCallback(
    async (id: string, status: TimePhasedAssumptionStatus) => {
      if (!scenarioId) return;
      const next = await setAssumptionHumanStatus(scenarioId, id, status);
      if (next) setAssumptions(next);
    },
    [scenarioId],
  );

  const applyRunOutput = useCallback(
    (out: Awaited<ReturnType<typeof runTimePhasedPlacement>>) => {
      setPlacements(out.placements);
      setUtilization(out.bucketUtilization ?? []);
      setAssumptions(out.assumptions);
      if (out.financial) {
        setMoneyUtilization(out.financial.moneyUtilization ?? []);
        setAffordability(out.financial.affordability ?? null);
      }
    },
    [],
  );

  const handleSetAnchoring = useCallback(
    async (priorityId: string, anchoring: PlacementAnchoring) => {
      if (!projectId || !scenarioId) return;
      setCounterLoading(true);
      setCounterError(null);
      try {
        await setPriorityAnchoring(priorityId, anchoring);
        applyRunOutput(await runTimePhasedPlacement(projectId, scenarioId));
      } catch (err) {
        setCounterError(err instanceof Error ? err.message : 'Failed to update anchoring');
      } finally {
        setCounterLoading(false);
      }
    },
    [projectId, scenarioId, applyRunOutput],
  );

  const handleShiftWeeks = useCallback(
    async (priorityId: string, deltaWeeks: number) => {
      if (!projectId || !scenarioId || deltaWeeks === 0) return;
      setCounterLoading(true);
      setCounterError(null);
      try {
        await shiftPriorityLaunchWeeks(priorityId, deltaWeeks);
        applyRunOutput(await runTimePhasedPlacement(projectId, scenarioId));
      } catch (err) {
        setCounterError(err instanceof Error ? err.message : 'Failed to shift launch');
      } finally {
        setCounterLoading(false);
      }
    },
    [projectId, scenarioId, applyRunOutput],
  );

  useEffect(() => {
    if (!selectedId || !inspectorRef.current) return;
    inspectorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);

  useEffect(() => {
    if (!projectId) return;
    void getScenarios(projectId).then((list) => {
      setScenarios(list);
      const active = list.find((s) => s.active) ?? list[0];
      if (active) setScenarioId((cur) => cur || active.id);
    });
  }, [projectId]);

  // Read persisted placements + utilization; backfill funding when calendar exists but FIN snapshot doesn't.
  const loadPersisted = useCallback(async () => {
    if (!scenarioId) return;
    setLoading(true);
    setError(null);
    try {
      const [rows, util, latestChange, financial, assumptionSnap] = await Promise.all([
        getLatestPlacements(scenarioId),
        getLatestUtilization(scenarioId).catch(() => [] as BucketUtilization[]),
        getLatestCalendarChangeSet(scenarioId).catch(() => null),
        getLatestFinancialUtilization(scenarioId).catch(() => ({
          moneyUtilization: [] as BucketMoneyUtilization[],
          affordability: null,
        })),
        getLatestTimePhasedAssumptions(scenarioId).catch(() => null),
      ]);
      setPlacements(rows);
      setUtilization(util);
      setAssumptions(assumptionSnap);
      setHasPersisted(rows.length > 0);
      if (latestChange) setChangeSet(latestChange);

      const hasMoney =
        financial.affordability != null &&
        (financial.affordability.totalApproved > 0 ||
          financial.affordability.totalForecastDemand > 0);

      if (rows.length > 0 && projectId && !hasMoney) {
        setFundingLoading(true);
        try {
          const ensured = await ensureScenarioFinancialLayer(projectId, scenarioId, rows);
          setMoneyUtilization(ensured?.moneyUtilization ?? []);
          setAffordability(ensured?.affordability ?? null);
        } catch {
          setMoneyUtilization(financial.moneyUtilization);
          setAffordability(financial.affordability);
        } finally {
          setFundingLoading(false);
        }
      } else {
        setMoneyUtilization(financial.moneyUtilization);
        setAffordability(financial.affordability);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load launch calendar');
    } finally {
      setLoading(false);
    }
  }, [scenarioId, projectId]);

  useEffect(() => {
    void loadPersisted();
  }, [loadPersisted]);

  const handleRun = useCallback(async () => {
    if (!projectId || !scenarioId) return;
    setRunning(true);
    setError(null);
    try {
      const out = await runTimePhasedPlacement(projectId, scenarioId);
      setPlacements(out.placements);
      setUtilization(out.bucketUtilization);
      setMoneyUtilization(out.financial?.moneyUtilization ?? []);
      setAffordability(out.financial?.affordability ?? null);
      setAssumptions(out.assumptions);
      setHasPersisted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Placement run failed');
    } finally {
      setRunning(false);
    }
  }, [projectId, scenarioId]);

  const handleFirstRunSetup = useCallback(async () => {
    if (!projectId || !scenarioId || !scenario) return;
    setRunning(true);
    setError(null);
    try {
      const out = await setupTimePhasedPlanning(projectId, scenarioId, scenario.planningWeeks);
      setPlacements(out.placements);
      setUtilization(out.bucketUtilization);
      setMoneyUtilization(out.financial?.moneyUtilization ?? []);
      setAffordability(out.financial?.affordability ?? null);
      setAssumptions(out.assumptions);
      setHasPersisted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Setup failed');
    } finally {
      setRunning(false);
    }
  }, [projectId, scenarioId, scenario]);

  const handleKeithDemo = useCallback(async () => {
    if (!projectId) return;
    setDemoLoading(true);
    setRunning(true);
    setError(null);
    try {
      const { scenario: keithScenario, output, message } = await seedKeithSharedTeamCommit(
        projectId,
        scenario?.planningWeeks ?? 13,
      );
      const list = await getScenarios(projectId);
      setScenarios(list);
      setScenarioId(keithScenario.id);
      setPlacements(output.placements);
      setUtilization(output.bucketUtilization);
      setMoneyUtilization(output.financial?.moneyUtilization ?? []);
      setAffordability(output.financial?.affordability ?? null);
      setAssumptions(output.assumptions);
      setHasPersisted(true);
      setDemoMessage(message);
      setChangeSet(null);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : e && typeof e === 'object' && 'message' in e
            ? String((e as { message: unknown }).message)
            : typeof e === 'string'
              ? e
              : JSON.stringify(e);
      setError(msg || 'Shared-team sample could not be loaded');
    } finally {
      setDemoLoading(false);
      setRunning(false);
    }
  }, [projectId, scenario?.planningWeeks]);

  const handleSamplePortfolio = useCallback(async () => {
    if (!projectId || !scenarioId || !scenario) return;
    setDemoLoading(true);
    setRunning(true);
    setError(null);
    try {
      const { output, message } = await seedSamplePortfolio(
        projectId,
        scenarioId,
        scenario.planningWeeks,
      );
      setPlacements(output.placements);
      setUtilization(output.bucketUtilization);
      setMoneyUtilization(output.financial?.moneyUtilization ?? []);
      setAffordability(output.financial?.affordability ?? null);
      setAssumptions(output.assumptions);
      setHasPersisted(true);
      setDemoMessage(message);
      setChangeSet(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sample portfolio seed failed');
    } finally {
      setDemoLoading(false);
      setRunning(false);
    }
  }, [projectId, scenarioId, scenario]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return placements;
    return placements.filter(
      (p) =>
        p.priorityName.toLowerCase().includes(q) ||
        (p.owningOrg?.toLowerCase().includes(q) ?? false),
    );
  }, [placements, search]);

  const countState = getItemCountState(placements.length);
  const summary = summarize(placements);
  const answer = useMemo(() => launchAnswer(placements), [placements]);
  const quarterLoad = useMemo(() => rollUpByQuarter(utilization), [utilization]);
  const periodConfidence = useMemo(() => {
    const withConf = placements.filter((p) => p.calendarConfidence != null);
    if (withConf.length === 0) return null;
    const avg =
      withConf.reduce((s, p) => s + (p.calendarConfidence ?? 0), 0) / withConf.length;
    const experimental = withConf.filter((p) => (p.calendarConfidence ?? 1) < 0.55).length;
    return { avg, experimental, n: withConf.length };
  }, [placements]);
  const assumedCount = placements.filter((p) => p.assumedProfile).length;
  const buildableNotLaunchable = placements.filter((p) => p.buildable && !p.launchable);
  const moneyHot = moneyUtilization.filter((b) => b.utilizationPct >= 85 || b.overage > 0);
  const hasFundingSignal =
    affordability != null &&
    (affordability.totalApproved > 0 || affordability.totalForecastDemand > 0);
  const pmmOverload = utilization
    .filter((b) => b.resourceType === 'marketing' && b.utilizationPct >= 85)
    .slice(0, 16);

  const handleSeedFunding = useCallback(async () => {
    if (!projectId || !scenarioId || placements.length === 0) return;
    setFundingLoading(true);
    setError(null);
    try {
      const ensured = await ensureScenarioFinancialLayer(projectId, scenarioId, placements);
      setMoneyUtilization(ensured?.moneyUtilization ?? []);
      setAffordability(ensured?.affordability ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to seed funding layer');
    } finally {
      setFundingLoading(false);
    }
  }, [projectId, scenarioId, placements]);

  const handleCounterfactual = async (lever: 'promote' | 'delay') => {
    if (!projectId || !scenarioId || !selectedId) return;
    setCounterLoading(true);
    setCounterError(null);
    setDemoMessage(null);
    try {
      const { changeSet: nextChangeSet } = await runCounterfactual(
        projectId,
        scenarioId,
        lever,
        selectedId,
        4,
        userId ?? undefined,
      );
      setChangeSet(nextChangeSet);
      void getScenarios(projectId).then(setScenarios);
    } catch (e) {
      setCounterError(e instanceof Error ? e.message : 'Counterfactual failed');
    } finally {
      setCounterLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h1 className="text-xl md:text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Launch Calendar
            </h1>
          </div>
          <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] max-w-2xl">
            Can we launch what we prioritized? Launch windows on shared teams — buildable vs
            launchable, slip, and counterfactuals.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {scenarios.length > 1 && (
            <select
              value={scenarioId}
              onChange={(e) => {
                setScenarioId(e.target.value);
                setUtilization([]);
                setChangeSet(null);
                setCounterError(null);
                setDemoMessage(null);
                setSelectedId('');
                setBaselineInput(null);
                setBaselineOutput(null);
              }}
              className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 min-w-[180px]"
              aria-label="Scenario"
            >
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <ScenarioBindingBadge scenario={scenario} />
          {hasPersisted && (
            <>
              <button
                type="button"
                onClick={() => void handleRun()}
                disabled={running || !scenarioId}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {running ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Re-run placement
              </button>
              {canLoadSamples && (
                <>
                  <button
                    type="button"
                    onClick={() => void handleKeithDemo()}
                    disabled={demoLoading || running || !projectId}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                    title="Platform admins only: create the shared-team sample scenario and Item Pool"
                  >
                    {demoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Rocket className="h-4 w-4" />
                    )}
                    Shared-team sample
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSamplePortfolio()}
                    disabled={demoLoading || running || !scenarioId}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                    title="Platform admins only: seed a multi-org sample portfolio"
                  >
                    {demoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Calendar className="h-4 w-4" />
                    )}
                    Sample portfolio
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() =>
                  downloadTextFile('launch-calendar.csv', exportPlacementsCsv(placements))
                }
                disabled={placements.length === 0}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className={`${CARD} p-4 text-sm text-[var(--coral)] dark:text-[var(--coral)] flex items-start gap-2`}>
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {loading || hasPersisted === null ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        </div>
      ) : !hasPersisted ? (
        <FirstRunCard
          onSetup={() => void handleFirstRunSetup()}
          onDemo={() => void handleKeithDemo()}
          onSample={() => void handleSamplePortfolio()}
          canLoadSamples={canLoadSamples}
          settingUp={running || demoLoading}
          error={error}
        />
      ) : (
        <>
          <ScenarioAssumptionsPanel
            snapshot={assumptions}
            baselineInput={baselineInput}
            baselineOutput={baselineOutput}
            onStatusChange={(id, status) => void handleAssumptionStatus(id, status)}
          />

          {/* The answer in one sentence — for a leader who reads nothing else (P-3) */}
          <div className={`${CARD} p-5`} data-testid="launch-answer">
            <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
              Can we launch what we prioritized?
            </h2>
            <p className="mt-2 text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {answer.sentence}
            </p>
            {answer.slipWeeks > 0 && (
              <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {summary.slipped} launch{summary.slipped === 1 ? '' : 'es'} slipped past the target window,{' '}
                {answer.slipWeeks} week{answer.slipWeeks === 1 ? '' : 's'} in all.
              </p>
            )}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Scheduled', value: summary.scheduled, icon: CheckCircle2, tone: 'text-[var(--green)]' },
              { label: 'Slipped', value: summary.slipped, icon: TrendingUp, tone: 'text-[var(--mustard)]' },
              { label: 'Blocked / exhausted', value: summary.blockedOrExhausted, icon: XCircle, tone: 'text-[var(--coral)]' },
              { label: 'Buildable ≠ launchable', value: summary.buildableNotLaunchable, icon: Rocket, tone: 'text-[var(--mustard)]' },
            ].map((stat) => (
              <div key={stat.label} className={`${CARD} p-4`}>
                <stat.icon className={`h-4 w-4 ${stat.tone} mb-2`} />
                <div className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {stat.value}
                </div>
                <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Funding — always visible (Planisware FIN peer of hours) */}
          <div
            className={`${CARD} p-5 ${
              hasFundingSignal && affordability && !affordability.withinEnvelope
                ? 'border-[var(--coral)] dark:border-[var(--coral)]/40'
                : ''
            }`}
          >
            <div className="flex items-start gap-3 mb-2">
              <div className="h-9 w-9 bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center shrink-0">
                <DollarSign className="h-5 w-5 text-primary-700 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                  Funding vs launch cost
                </h2>
                {fundingLoading ? (
                  <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Computing funding affordability…
                  </p>
                ) : hasFundingSignal && affordability ? (
                  <>
                    <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {affordability.withinEnvelope ? (
                        <>
                          Forecast demand{' '}
                          <strong>
                            ${Math.round(affordability.totalForecastDemand).toLocaleString()}
                          </strong>{' '}
                          fits approved envelope{' '}
                          <strong>
                            ${Math.round(affordability.totalApproved).toLocaleString()}
                          </strong>
                          .
                        </>
                      ) : (
                        <>
                          Portfolio exceeds funding by{' '}
                          <strong className="text-[var(--coral)] dark:text-[var(--coral)]">
                            ${Math.round(affordability.overageTotal).toLocaleString()}
                          </strong>{' '}
                          (approved ${Math.round(affordability.totalApproved).toLocaleString()} vs
                          forecast ${Math.round(affordability.totalForecastDemand).toLocaleString()}
                          ).
                        </>
                      )}
                    </p>
                    {!affordability.withinEnvelope &&
                      affordability.contributingPriorities.length > 0 && (
                        <ul className="text-sm space-y-1 mt-3 mb-1">
                          {affordability.contributingPriorities.slice(0, 5).map((c) => (
                            <li
                              key={c.priorityId}
                              className="text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                            >
                              {c.priorityName}{' '}
                              <span className="text-base font-mono text-[var(--text-ter)]">
                                ${Math.round(c.forecastDemand).toLocaleString()}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    {moneyHot.length > 0 && (
                      <>
                        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-3 mb-2">
                          Weeks where forecast spend hits or exceeds approved funding
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {moneyHot.slice(0, 26).map((b) => (
                            <div
                              key={b.bucketStart}
                              title={`${b.bucketStart}: $${Math.round(b.forecastDemand)} / $${Math.round(b.approvedSupply)} (${b.utilizationPct}%)`}
                              className={`h-8 w-8 rounded ${utilizationHeatClass(Math.min(b.utilizationPct, 150))}`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                    <Link
                      to="/planning/engine/funding"
                      className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Edit funding &amp; launch costs
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      No funding envelope or launch cost plans on this scenario yet. Enter weekly
                      approved funding and per-launch costs under Inputs → Funding &amp; Costs — or seed a
                      starter layer here.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link
                        to="/planning/engine/funding"
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white"
                      >
                        <DollarSign className="h-4 w-4" />
                        Enter funding &amp; costs
                      </Link>
                      <button
                        type="button"
                        onClick={() => void handleSeedFunding()}
                        disabled={fundingLoading || placements.length === 0}
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
                      >
                        Seed starter layer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Assumed-demand banner */}
          {assumedCount > 0 && (
            <div className={`${CARD} p-4 flex flex-col sm:flex-row sm:items-center gap-3 border-primary-200 dark:border-primary-800/40`}>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] flex-1">
                <strong>{assumedCount}</strong> placement{assumedCount === 1 ? '' : 's'} used{' '}
                <em>assumed demand</em> because no workload template is assigned. Assign real profiles
                for accurate calendars.
              </p>
              <Link
                to="/planning/engine/time-phased"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline shrink-0"
              >
                Open launch setup
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}

          {/* Timeline */}
          {countState !== 'zero' && (
            <div className={CARD}>
              <div className="p-4 border-b border-[var(--rule)] dark:border-[var(--rule)] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                    Timeline
                  </h2>
                  {periodConfidence && (
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Period confidence {(periodConfidence.avg * 100).toFixed(0)}% avg
                      {periodConfidence.experimental > 0
                        ? ` · ${periodConfidence.experimental} of ${periodConfidence.n} experimental / low-certainty`
                        : ` · ${periodConfidence.n} placements`}
                    </p>
                  )}
                </div>
                {countState === 'many' && (
                  <div className="relative max-w-xs w-full">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search priorities…"
                      className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
                    />
                  </div>
                )}
              </div>
              <LaunchTimeline
                placements={filtered}
                horizonStart={scenario?.horizonStartDate ?? undefined}
                horizonEnd={scenario?.horizonEndDate ?? undefined}
                selectedId={selectedId}
                onSelect={(id) => {
                  setSelectedId((cur) => (cur === id ? '' : id));
                }}
                onShiftWeeks={(id, delta) => void handleShiftWeeks(id, delta)}
              />
              <p className="px-4 pb-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                Click a row for detail. Drag flexible bars to shift; unlock fixed bars first.
              </p>
            </div>
          )}

          {selectedPlacement && (
            <div ref={inspectorRef}>
              <LaunchPlacementInspector
                placement={selectedPlacement}
                counterLoading={counterLoading}
                onClose={() => setSelectedId('')}
                onPromote={() => void handleCounterfactual('promote')}
                onDelay={() => void handleCounterfactual('delay')}
                onSetAnchoring={(anchoring) =>
                  void handleSetAnchoring(selectedPlacement.priorityId, anchoring)
                }
              />
            </div>
          )}

          {countState === 'zero' && (
            <div className={`${CARD} p-8 text-center text-sm text-[var(--text-ter)]`}>
              No priorities in scope. Add priorities in the Planning Engine first.
              <Link
                to="/planning/engine/priorities"
                className="block mt-2 text-primary-600 hover:underline"
              >
                Open priorities →
              </Link>
            </div>
          )}

          {/* Buildable but not launchable */}
          {buildableNotLaunchable.length > 0 && (
            <div className={`${CARD} p-5 border-[var(--mustard)] dark:border-[var(--mustard)]/40`}>
              <h2 className="text-sm font-semibold text-[var(--mustard)] dark:text-[var(--mustard)] uppercase tracking-wider mb-2">
                Buildable but not launchable
              </h2>
              <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">
                Engineering can ship these — shared GTM teams (PMM, Legal, CS) cannot support the
                launch window.
              </p>
              <ul className="space-y-1 text-sm">
                {buildableNotLaunchable.slice(0, 5).map((p) => (
                  <li key={p.priorityId} className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {p.priorityName}
                    {p.slipWeeks ? ` — slipped ${p.slipWeeks} weeks` : ''}
                  </li>
                ))}
              </ul>
              <Link
                to="/planning/engine/participation?from=launch-calendar"
                className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                Confirm team participation
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}

          {/* Shared-team capacity by quarter (P-1) */}
          {quarterLoad.teams.length > 0 && (
            <div className={`${CARD} p-5`} data-testid="quarter-load">
              <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-1">
                Shared-team capacity by quarter
              </h2>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
                Hours each team is asked for against the hours it has, per calendar quarter. Busiest team first.
              </p>
              <DataTable
                density="compact"
                rule="horizontal"
                ariaLabel="Shared-team capacity by quarter"
                rows={quarterLoad.teams}
                getRowKey={(t) => t.resourceType}
                columns={[
                  { key: 'resourceType', header: 'Team' },
                  ...quarterLoad.quarters.map((q) => ({
                    key: q.key,
                    header: q.label,
                    render: (t: (typeof quarterLoad.teams)[number]) => {
                      const cell = t.quarters.find((c) => c.key === q.key);
                      return cell ? (
                        <span
                          className="inline-flex items-center gap-2 tabular-nums"
                          title={`${Math.round(cell.demandHours)} of ${Math.round(cell.supplyHours)} hours`}
                        >
                          <span className={`h-3 w-3 rounded-sm ${utilizationHeatClass(cell.utilizationPct)}`} />
                          {cell.supplyHours > 0 ? `${cell.utilizationPct}%` : 'No hours'}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--text-ter)]">—</span>
                      );
                    },
                  })),
                ]}
              />
            </div>
          )}

          {/* PMM overload heat */}
          {pmmOverload.length > 0 && (
            <div className={`${CARD} p-5`}>
              <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-2">
                Product Marketing utilization
              </h2>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
                Weeks at ≥85% capacity — the &ldquo;marketing can&apos;t support it&rdquo; signal.
              </p>
              <div className="flex flex-wrap gap-1">
                {pmmOverload.map((b) => (
                  <div
                    key={b.bucketStart}
                    title={`${b.bucketStart}: ${b.utilizationPct}%`}
                    className={`h-8 w-8 rounded ${utilizationHeatClass(b.utilizationPct)}`}
                  />
                ))}
              </div>
              <Link
                to="/planning/engine/time-phased"
                className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                Edit supply calendar
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}

          {(changeSet || demoMessage || counterError) && (
            <div className={`${CARD} p-5`}>
              <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-3">
                Calendar change set
              </h2>
              {demoMessage && (
                <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-3">{demoMessage}</p>
              )}
              {counterError && <p className="text-sm text-[var(--coral)] mb-3">{counterError}</p>}
              {changeSet && (
                <CalendarChangeSetPanel
                  changeSet={changeSet}
                  onOpenHypothesis={(id) => {
                    setScenarioId(id);
                    setSelectedId('');
                  }}
                />
              )}
            </div>
          )}

          {/* Status list (compact) — clickable, mirrors timeline selection */}
          {countState !== 'zero' && (
            <div className={CARD}>
              <div className="p-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
                <h2 className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] uppercase tracking-wider">
                  All placements
                </h2>
              </div>
              <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {filtered.map((p) => {
                  const isSelected = selectedId === p.priorityId;
                  return (
                    <button
                      key={p.priorityId}
                      type="button"
                      onClick={() =>
                        setSelectedId((cur) => (cur === p.priorityId ? '' : p.priorityId))
                      }
                      className={`w-full text-left px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${
                        isSelected
                          ? 'bg-primary-50/70 dark:bg-primary-900/15'
                          : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/[0.03]'
                      }`}
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                          {p.priorityName}
                        </span>
                        {p.owningOrg && (
                          <span className="ml-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                            {p.owningOrg}
                          </span>
                        )}
                        <div className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                          {p.feasibleStart && p.feasibleEnd
                            ? `${p.feasibleStart} → ${p.feasibleEnd}`
                            : 'No feasible window'}
                          {p.slipWeeks != null && p.slipWeeks > 0 && (
                            <span className="text-[var(--mustard)] dark:text-[var(--mustard)] ml-2">
                              +{p.slipWeeks} wk slip
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 shrink-0">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${PLACEMENT_STATUS_CLASS[p.placementStatus]}`}
                        >
                          {PLACEMENT_STATUS_LABELS[p.placementStatus]}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            p.buildable
                              ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/20'
                              : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
                          }`}
                        >
                          {p.buildable ? 'Buildable' : 'Not buildable'}
                        </span>
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            p.launchable
                              ? 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/20 dark:text-[var(--text-ter)]'
                              : 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]'
                          }`}
                        >
                          {p.launchable ? 'Launchable' : 'Not launchable'}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function LaunchCalendarPage() {
  return (
    <TimePhasedModuleGate>
      <LaunchCalendarInner />
    </TimePhasedModuleGate>
  );
}
